import _ from 'lodash';
import React, { useMemo, useRef, useState } from "react";
import { generateClient } from '@aws-amplify/api';
import { getRecruitmentSources as _getRecruitmentSources } from '@/graphql/queries';
import { upsertRecruitmentSource as _upsertRecruitmentSource } from '@/graphql/mutations';
import { isValidPhoneNumber } from 'react-phone-number-input';
import HeaderWithTabs from '@/HeaderWithTabs'
import ErrorModal from '@/ErrorModal';
import Breadcrumb from '@/Breadcrumb';
import { phoneField, selectField, textField } from '@/commonFields';
import useFormFields from '@/hooks/useFormFields';
import { states} from '../SubjectEntry/enums';

const client = generateClient();

const getObject = ({ id }) => {
	return client.graphql({ query: _getRecruitmentSources, variables: { id } })
		.then(response => {
			const data = response.data.getRecruitmentSources.recruitment_sources?.[0];
			return data;
		});
};

const upsertObject = ({ input }) => {
	return client.graphql({ query: _upsertRecruitmentSource, variables: { input } })
		.then(response => {
			return response.data.upsertRecruitmentSource;
		});
};

const NO_PARENT = {
	source_description: 'Does not have a parent',
	id: null,
	source_name: 'No parent'
};

const getParentRecruitmentSources = ({ id }) => {
	const filters = [{
		// Not ourselves
		...(id ? { id: { ne: id } } : undefined),
		// This filter gets us recruitment_sources to choose from which aren't children.
		// FIXME: We need to enforce in the trigger that a parent_id cannot be set to a
		// parent which is a child. And likewise we mustn't be able to set a parent as
		// a child.
		parent_id: { eq: null }
	}];
	return client.graphql({ query: _getRecruitmentSources, variables: { filters, sorting: { field: 'source_name', order: 'asc' } } })
		.then(response => {
			return [
				NO_PARENT,
				...response.data.getRecruitmentSources.recruitment_sources
			];
		});
};

export default ({ createLabel = 'Create', links = [], onCancel, onSave, updateLabel = 'Save' }) => {
	const {
		origModel,
		model,
		setModel,
		changeModel,
		deleteModel,
		jsonModel,
		errors,
		setErrors,
		hasValue,
		getError,
		get_value,
		set_value,
		get_array,
		set_array,
		add_array,
		remove_array,
		id,
		isCreate,
		sanitizeObject
	} = useFormFields();

	const isConstructed = useRef(false);
	const [errorMessage, setErrorMessage] = useState();
	const [isLoaded, setIsLoaded] = useState(isCreate);
	const [isSaving, setIsSaving] = useState(false);
	const isEqual = _.isEqualWith(sanitizeObject(model), origModel.current);

	const catchError = error => {
		if (error instanceof Error) {
			setErrorMessage(error.toString());
		} else {
			setErrorMessage((error.errors || []).map(e => e.message?.toString()));
		}
		setIsLoaded(true);
		setIsSaving(false);
	};

	if (!isConstructed.current) {
		isConstructed.current = true;
		if (!isLoaded) {
			getObject({ id })
				.then(data => {
					origModel.current = { recruitment_sources: data, subjects: {} };
					setModel({ recruitment_sources: data, subjects: {} });
					setIsLoaded(true);
				})
				.catch(catchError);
		}
	}

	const details = useMemo(() => (
		<div className="row mt-5">
			<div className="col-6">
				<div className="row">
					<div className="col">
						<div className="mb-3"><b><u>Source Information</u></b></div>
						<div className="border shadow p-3 bg-form">
							{textField({ autoFocus: isCreate, get: get_value, getError, set: set_value, key: 'recruitment_sources', label: 'Source Name', name: 'source_name' })}
							{textField({ get: get_value, getError, set: set_value, key: 'recruitment_sources', label: 'Source Description', name: 'source_description' })}
							{selectField({ defaultOptions: model.recruitment_sources?.parent_id ? [NO_PARENT, { id: model.recruitment_sources.parent_id, source_description: model.recruitment_sources.parent_id_source_description, source_name: model.recruitment_sources.parent_id_source_name }] : [], get: get_value, getData: () => getParentRecruitmentSources({ id: model.recruitment_sources?.id }), getError, set: set_value, key: 'recruitment_sources', label: 'Parent', label1Key: 'source_name', label2Key: 'source_description', name: 'parent_id', idKey: 'id', valueKey: 'id' })}
							<div className="row">
								<div className="col-6">
									{textField({ get: get_value, getError, set: set_value, key: 'recruitment_sources', label: 'Total Spend', name: 'total_spend' })}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="col-6">
				<div className="mb-3"><b><u>Contact Information</u></b></div>
				<div className="border shadow p-3 bg-form">
					<div className="row">
						<div className="col">
							{textField({ get: get_value, getError, set: set_value, key: 'recruitment_sources', label: 'Contact Name', name: 'contact_name' })}
						</div>
						<div className="col">
							{textField({ get: get_value, getError, set: set_value, key: 'recruitment_sources', label: 'Contact Email Address', name: 'contact_email_address' })}
						</div>
						<div className="col">
							{phoneField({ get: get_value, getError, set: set_value, key: 'recruitment_sources', label: 'Contact Phone Number', name: 'contact_phone_number' })}
						</div>
					</div>
					{textField({ get: get_value, getError, set: set_value, key: 'recruitment_sources', label: 'Contact Address Line 1', name: 'contact_address_line1' })}
					{textField({ get: get_value, getError, set: set_value, key: 'recruitment_sources', label: 'Contact Address Line 2', name: 'contact_address_line2' })}
					<div className="row">
						<div className="col">
							{textField({ get: get_value, getError, set: set_value, key: 'recruitment_sources', label: 'Contact City', name: 'contact_city' })}
						</div>
						<div className="col">
							{selectField({ get: get_value, getError, set: set_value, key: 'recruitment_sources', label: 'Contact State', label1Key: 'id', label2Key: 'label', name: 'contact_state', options: states })}
						</div>
						<div className="col">
							{textField({ get: get_value, getError, set: set_value, key: 'recruitment_sources', label: 'Contact Postal Code', name: 'contact_postal_code' })}
						</div>
					</div>
				</div>
			</div>
		</div>
	), [JSON.stringify(errors), JSON.stringify(model)]);

	const tabs = [
		{ name: 'details', label: 'Details', content: details }
	];

	const handleSave = () => {
		// Extract input from model
		const input = {
			...changeModel.current,
			recruitment_sources: {
				...changeModel.current.recruitment_sources,
				...(!isCreate ? { id } : undefined)
			}
		};
		// Remove this -- it's for our mocked up segment creation
		delete input.subjects;
		console.log('model', model);
		const newErrors = {
			recruitment_sources: {}
		};
		if (!hasValue({ key: 'recruitment_sources', name: 'source_name' })) {
			newErrors.recruitment_sources.source_name = 'Source Name is required';
		}
		// Not required but must be valid
		if (model.recruitment_sources?.contact_phone_number && !isValidPhoneNumber(model.recruitment_sources.contact_phone_number)) {
			newErrors.recruitment_sources = { ...newErrors.recruitment_sources, contact_phone_number: 'Must have valid Phone Number' };
		}
		// Clean out empties
		Object.keys(newErrors).forEach(e => {
			if (
				(Array.isArray(newErrors[e]) && newErrors[e].length === 0) ||
				Object.keys(newErrors[e]).length === 0
			) {
				delete newErrors[e];
			}
		});
		console.log('newErrors', newErrors);
		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}
		console.log('input', input);
		console.log('deleteModel', deleteModel.current);
		setIsSaving(true);
		upsertObject({ input, ...(deleteModel.current.length > 0 ? { input_delete: deleteModel.current } : undefined) })
			.then(response => {
				console.log('response', response);
				setIsSaving(false);
				onSave && onSave();
			})
			.catch(catchError);
	};

	const handleCancel = () => onCancel && onCancel();

	return (
		<>
			{errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage()} title="Error" />}
			<div className="container-fluid px-4 position-relative">
				<HeaderWithTabs
					isLoaded={isLoaded}
					path="/app/customers/"
					tabs={tabs}
				>
					<>
						{!isLoaded ? (
							<div className="text-center" style={{marginTop:'5rem'}}>
								<button className="btn btn-primary" type="button" disabled>
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									Loading...
								</button>
							</div>
						) : (
							<>
								{links.length > 0 && <Breadcrumb className="mb-3 px-3" links={links} />}
								<div className="row align-items-center position-absolute py-1 me-5" style={{right:0}}>
									<button className="col-auto ml-auto my-0 btn btn-primary" disabled={!((isCreate || !isEqual) && !isSaving)} onClick={handleSave}>{isCreate ? createLabel : updateLabel }</button>
									<a className="col-auto" role="button" onClick={handleCancel}>Cancel</a>
								</div>
								{isSaving && (
									<div className="text-center position-absolute" style={{height:'100%',left:0,top:0, width:'100%',zIndex:1}}>
										<button className="btn btn-primary position-relative" style={{top:'40%'}} type="button" disabled>
											<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
											Saving...
										</button>
									</div>
								)}
							</>
						)}
					</>
				</HeaderWithTabs>
			</div>
		</>
	);
};
